import { NEW_FEVER_HOST, FEVER_API_ENDPOINT } from 'config/envVariable';
import { getFirebaseIdToken } from 'editor/utils/firebaseManager';
import { isJSONParsable } from 'common/utils/objectManipulate';

export function fetchOpts({
    method,
    token = '',
    body = null,
    mode = '',
    recaptchaToken = null,
    sponsorId = ''
}) {
    return {
        method,
        headers: new Headers({
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
            'Fever-Host': NEW_FEVER_HOST,
            'Fever-Mode': mode,
            sponsorId,
            recaptchaToken,
            ...(token
                ? {
                      Authorization: `Bearer ${token}`
                  }
                : {})
        }),
        body
    };
}

export function newFetchOpts({
    method = 'GET',
    headers = {},
    body = null,
    formData,
    apiVersion = 1,
    mode,
    token = '',
    ...rest
} = {}) {
    return {
        method,
        headers: new Headers({
            'Fever-Host': NEW_FEVER_HOST,
            'Fever-Mode': mode,
            Accept: `application/x.api.v${apiVersion}+json`,
            ...(token
                ? {
                      Authorization: `Bearer ${token}`
                  }
                : {}),
            ...(formData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
            ...headers
        }),
        body: formData ? formData : body && JSON.stringify(body),
        ...rest
    };
}

export function isTempId(input) {
    return /^temp_/.test(input);
}

export function isInIframe() {
    if (window.self !== window.top) {
        return true;
    }
    return false;
}

export async function normalizeResponse(resp) {
    const rawPayload = await resp.text();
    try {
        const payload = JSON.parse(rawPayload);
        return { httpStatus: resp.status, headers: resp.headers, payload };
    } catch {
        return { httpStatus: resp.status, headers: resp.headers, rawPayload };
    }
}

export async function fetchPromo(
    path,
    optionsPromise,
    { dataKeys = 'data', strictErrThrow = true, customErrorHandler } = {}
) {
    const options = await optionsPromise;
    const baseUrl = FEVER_API_ENDPOINT;
    return fetch(`${baseUrl}${path}`, options)
        .then(normalizeResponse)
        .then(resp => {
            const generalErrorHandler = () => {
                if (!strictErrThrow) {
                    return { ...resp, payload: resp.payload[dataKeys] };
                }
                if (resp.httpStatus >= 400) {
                    throw resp;
                }
                return resp.payload[dataKeys];
            };

            if (typeof customErrorHandler === 'function') {
                return customErrorHandler({ resp, dataKeys, generalErrorHandler });
            }
            return generalErrorHandler();
        });
}

export async function generateFetchOpts({
    method = 'GET',
    headers = {},
    body = null,
    formData,
    apiVersion = 1,
    feverHost,
    ...rest
} = {}) {
    return {
        method,
        headers: new Headers({
            'Fever-Host': feverHost || NEW_FEVER_HOST,
            Accept: `application/x.api.v${apiVersion}+json`,
            Authorization: `Bearer ${await getFirebaseIdToken()}`,
            ...(body ? { 'Content-Type': 'application/json; charset=utf-8' } : {}),
            ...headers
        }),
        body: formData ? formData : body && JSON.stringify(body),
        ...rest
    };
}

export function generateApiErrObj(apiErr = {}, type, body) {
    let errObj = new Error(type);
    apiErr.errType = type;
    errObj.extraInfo = apiErr;

    if (!Array.isArray(apiErr) && typeof apiErr === 'object' && Boolean(body)) {
        errObj.extraInfo.body = body;
    }

    return errObj;
}

export function throwError(resp, throwErrConfigs = [], body = '') {
    const configs = Object.entries(throwErrConfigs);
    for (const [errType, checker] of configs) {
        if (typeof checker === 'function' && checker(resp)) {
            const apiBody = isJSONParsable(body) ? JSON.parse(body) : body;
            throw generateApiErrObj(resp, errType, apiBody);
        }
    }
}
